import { isNil } from "lodash";

import { DateFormatIdentifiers } from "../../../../constants/dateFormats";
import { DateOrderIdentifiers } from "../../../../constants/dateOrders";
import { TimeFormatIdentifiers } from "../../../../constants/timeFormats";
import calendarTypes from "../../../../types/calendarTypes";
import Precision from "../../../../types/datePrecision";
import { fourDigits, twoDigits } from "../../../../utils/numberToString";
import { getHoursInDayFromCalendar } from "../selectors";
import { isDateValid } from "./";
import dateByAddingSpan from "./dateByAddingSpan";
import dateSubtractionFunctions from "./dateSubtractionFunctions";
import { getDaysSinceZeroDate, getHoursSinceZeroDate, getWeeksSinceZeroDate } from "./stringFromZeroDate";
import { getReverseTimespan, timespanForComponents, timespanForPrecision } from "./timespanFunctions";

// ===================================================
// Exported Types
// ===================================================

const defaultOptionsFromCalendar = calendar => {
  return {
    calendarType: calendar.calendarType,
    dateFormat: calendar.dateFormat ? calendar.dateFormat : DateFormatIdentifiers.Long,
    timeFormat: calendar.timeFormat ? calendar.timeFormat : TimeFormatIdentifiers.Twelve,
    dateOrder: calendar.dateOrder ? calendar.dateOrder : DateOrderIdentifiers.DayMonthYear,
    includeDayName: isNil(calendar.dateFormatIncludesDayName) ? true : calendar.dateFormatIncludesDayName,
    customFormat: null,
    isCSVFormat: false
  };
};

// ===================================================
// Exported Functions
// ===================================================

export const getMonthName = (calendar, month) => calendar.getIn(["months", month - 1, "name"]);
export const getShortMonthName = (calendar, month) => calendar.getIn(["months", month - 1, "shortName"]);
export const getWeekdayName = (calendar, weekday) => calendar.getIn(["weekdays", weekday, "name"]);
export const getShortWeekdayName = (calendar, weekday) => calendar.getIn(["weekdays", weekday, "shortName"]);
export const getEraName = (calendar, era) => calendar.getIn(["eras", era, "name"]);
export const getEraNameIfNotLast = (calendar, era) =>
  era === calendar.get("eras").count() - 1 ? "" : calendar.getIn(["eras", era, "name"]);

export const getShortEraName = (calendar, era) => calendar.getIn(["eras", era, "shortName"]);
export const getShortEraNameIfNotLast = (calendar, era) =>
  era === calendar.get("eras").count() - 1 ? "" : getShortEraName(calendar, era);

export const getStringByStandardFormats = (calendar, date, options, is2400 = false) => {
  const dateStr = dateStringFromDate(calendar, date, options);
  const timeStr = timeStringFromDate(calendar, date, options, is2400);
  if (options.calendarType === calendarTypes.CALENDAR_TYPE_DATE_TIME) {
    const eraStr = eraStringFromDate(calendar, date, true);
    return `${dateStr}${eraStr ? " " + eraStr : ""}${timeStr ? " " + timeStr : ""}`;
  } else {
    return options.isCSVFormat
      ? `${dateStr}${timeStr && dateStr ? " " : ""}${timeStr}`
      : `${timeStr}${timeStr && dateStr ? " " : ""}${dateStr}`;
  }
};

export const stringFromDate = (calendar, date, inOptions) => {
  const options = Object.assign(defaultOptionsFromCalendar(calendar), inOptions ? inOptions : {});

  if (options.dateFormat === DateFormatIdentifiers.Custom) {
    return customDateStringFromDate(calendar, date, options.customFormat);
  }

  // If we reach here, we have one of the standard formats...
  return getStringByStandardFormats(calendar, date, options);
};

export const getIs2400 = (startDate, endDate, timeFormat) =>
  (!startDate || startDate.timestamp !== endDate.timestamp) &&
  (endDate.precision <= Precision.Day ||
    (endDate.hour === 0 &&
      endDate.minute === 0 &&
      endDate.second === 0 &&
      timeFormat === TimeFormatIdentifiers.TwentyFour));

export const adjustEndDateWhenDisplay = (calendar, date, startDate, timeFormat) => {
  const adjustmentPrecision = date.precision >= Precision.Week ? Precision.Day : date.precision;
  return getIs2400(startDate, date, timeFormat)
    ? dateByAddingSpan(calendar, date, getReverseTimespan(timespanForPrecision(adjustmentPrecision)))
    : date;
};

export const stringFromEndDate = (calendar, date, startDate, inOptions) => {
  const options = Object.assign(defaultOptionsFromCalendar(calendar), inOptions ? inOptions : {});

  const useDate = adjustEndDateWhenDisplay(calendar, date, startDate, options.timeFormat);
  // if the date changed, we want to show 24:00 as the time
  const is2400 = useDate !== date;

  if (options.dateFormat === DateFormatIdentifiers.Custom) {
    let useFormat = is2400
      ? options.customFormat.replace("%H", "24").replace("%M", "00").replace("%S", "00")
      : options.customFormat;
    return customDateStringFromDate(calendar, useDate, useFormat);
  }

  // If we reach here, we have one of the standard formats...
  return getStringByStandardFormats(calendar, useDate, options, is2400);
};

export const scrivenerStringFromEndDate = (calendar, date, startDate, inOptions) => {
  const options = Object.assign(defaultOptionsFromCalendar(calendar), inOptions ? inOptions : {});

  const useDate = getIs2400(startDate, date, TimeFormatIdentifiers.TwentyFour)
    ? dateByAddingSpan(calendar, date, timespanForComponents(0, 0, 0, 0, 0, 0, -1))
    : date;

  // if the date changed, we want to show 24:00 as the time
  const is2400 = useDate !== date;

  if (options.dateFormat === DateFormatIdentifiers.Custom) {
    return customDateStringFromDate(calendar, useDate, options.customFormat);
  }

  // If we reach here, we have one of the standard formats...
  return getStringByStandardFormats(calendar, useDate, options, is2400);
};

export const stringFromDateRange = (
  calendar,
  startDate,
  endDate,
  inOptions,
  adjustEndDateFor2400 = true,
  hasUncertainStart,
  hasUncertainEnd,
  ongoing,
  hasOngoingDescendants,
  dateForNow
) => {
  const options = Object.assign(defaultOptionsFromCalendar(calendar), inOptions ? inOptions : {});

  if (hasOngoingDescendants && isDateValid(dateForNow) && endDate.get("timestamp") <= dateForNow.get("timestamp")) {
    ongoing = true;
  }

  if (startDate.get("timestamp") === endDate.get("timestamp")) {
    return `${stringFromDate(calendar, startDate, options)}${ongoing ? " - Present" : ""}`;
  }

  const isAbsoluteDate = options.calendarType === calendarTypes.CALENDAR_TYPE_DATE_TIME;

  if (
    options.dateFormat === DateFormatIdentifiers.Custom ||
    (options.dateFormat === DateFormatIdentifiers.Short && isAbsoluteDate) ||
    (startDate.precision !== endDate.precision &&
      (startDate.precision <= Precision.Day || endDate.precision <= Precision.Day))
  ) {
    let returnStr = `${hasUncertainStart ? "~" : ""}${stringFromDate(calendar, startDate, options)} - `;
    if (ongoing) {
      returnStr += "Present";
    } else {
      const endStr = adjustEndDateFor2400
        ? stringFromEndDate(calendar, endDate, startDate, options)
        : stringFromDate(calendar, endDate, options);
      returnStr += hasUncertainEnd ? "~" : "";
      returnStr += endStr;
    }
    return returnStr;
  }

  return isAbsoluteDate
    ? stringFromAbsoluteDateRange(
      calendar,
      startDate,
      endDate,
      options,
      adjustEndDateFor2400,
      hasUncertainStart,
      hasUncertainEnd,
      ongoing
    )
    : stringFromRelativeDateRange(calendar, startDate, endDate, options, hasUncertainStart, hasUncertainEnd, ongoing);
};

// ===================================================
// Local Helper Functions
// ===================================================

const getPrintedYear = year =>
  year >= 1000000000
    ? Math.round((year / 1000000000) * 100) / 100 + " billion"
    : year >= 1000000
      ? Math.round((year / 1000000) * 100) / 100 + " million"
      : String(year);
const getShortPrintedYear = year =>
  year >= 1000000000
    ? Math.round((year / 1000000000) * 100) / 100 + "b"
    : year >= 1000000
      ? Math.round((year / 1000000) * 100) / 100 + "m"
      : String(year);
const getShortenedYear = year => year % 100;
const getTwelveHourString = (hour, hoursInDay) =>
  hour === 0 ? hoursInDay / 2 : hour > hoursInDay / 2 ? hour - hoursInDay / 2 : hour;
const getAmPmString = (hour, hoursInDay) => (hour >= hoursInDay / 2 ? "pm" : "am");
const getWeekString = week => `Week ${week}`;
const getDayString = day => `Day ${day}`;

const customDateStringFromDate = (calendar, date, customFormat) =>
  customFormat
    .replace("%d", twoDigits(date.get("day")))
    .replace("%e", date.get("day"))
    .replace("%m", twoDigits(date.get("month")))
    .replace("%n", date.get("month"))
    .replace("%g", getShortPrintedYear(date.get("year")))
    .replace("%G", getPrintedYear(date.get("year")))
    .replace("%y", getShortenedYear(date.get("year")))
    .replace("%Y", date.get("year"))
    .replace("%F", fourDigits(date.get("year")))
    .replace("%b", getShortMonthName(calendar, date.get("month")))
    .replace("%B", getMonthName(calendar, date.get("month")))
    .replace("%i", getWeekdayName(calendar, date.get("weekday")).charAt(0))
    .replace("%a", getShortWeekdayName(calendar, date.get("weekday")))
    .replace("%A", getWeekdayName(calendar, date.get("weekday")))
    .replace("%t", getShortEraName(calendar, date.get("era")))
    .replace("%T", getEraName(calendar, date.get("era")))
    .replace("%E", getEraNameIfNotLast(calendar, date.get("era")))
    .replace("%H", twoDigits(date.get("hour")))
    .replace("%I", getTwelveHourString(date.get("hour"), getHoursInDayFromCalendar(calendar)))
    .replace("%p", getAmPmString(date.get("hour"), getHoursInDayFromCalendar(calendar)))
    .replace("%M", twoDigits(date.get("minute")))
    .replace("%S", twoDigits(date.get("second")))
    .replace("%Z", getDayString(getDaysSinceZeroDate(calendar, date)))
    .replace("%z", getDaysSinceZeroDate(calendar, date))
    .replace("%X", getTimeStringForRelativeTime(calendar, date))
    .replace("%x", getHoursSinceZeroDate(calendar, date))
    .replace("%w", getWeeksSinceZeroDate(calendar, date))
    .replace("%W", getWeekString(getWeeksSinceZeroDate(calendar, date)));

const dateStringFromDate = (calendar, date, options) =>
  options.dateFormat === DateFormatIdentifiers.Long || options.isCSVFormat
    ? longDateStringFromDate(calendar, date, options)
    : options.dateFormat === DateFormatIdentifiers.Medium
      ? mediumDateStringFromDate(calendar, date, options)
      : shortDateStringFromDate(calendar, date, options.dateOrder, options);

const eraStringFromDate = (calendar, date, hideLast) =>
  hideLast ? getShortEraNameIfNotLast(calendar, date.era) : getShortEraName(calendar, date.era);

const longDateStringFromDate = (calendar, date, options) => {
  if (options.calendarType === calendarTypes.CALENDAR_TYPE_DATE_TIME) {
    // We have not checked the date order here
    switch (date.get("precision")) {
      case Precision.Century:
      case Precision.Decade:
      case Precision.Year:
        return `${getPrintedYear(date.get("year"))}`;
      case Precision.Month:
        return `${getMonthName(calendar, date.get("month"))} ${getPrintedYear(date.get("year"))}`;
      default:
        return `${options.includeDayName ? getWeekdayName(calendar, date.get("weekday")) + " " : ""}${date.get(
          "day"
        )} ${getMonthName(calendar, date.get("month"))} ${getPrintedYear(date.get("year"))}`;
    }
  } else if (options.calendarType === calendarTypes.CALENDAR_TYPE_WEEKDAY_TIME) {
    switch (date.get("precision")) {
      case Precision.Century:
      case Precision.Decade:
      case Precision.Year:
      case Precision.Month:
      case Precision.Week:
        return `Week ${getWeeksSinceZeroDate(calendar, date)}`;
      default: {
        const weekDayName = getWeekdayName(calendar, date.get("weekday"));
        const week = getWeeksSinceZeroDate(calendar, date);
        return options.isCSVFormat ? `Week ${week} ` + `${weekDayName}` : `${weekDayName + " "}` + `Week ${week}`;
      }
    }
  } else if (options.calendarType === calendarTypes.CALENDAR_TYPE_DAY_TIME) {
    return `Day ${getDaysSinceZeroDate(calendar, date)}`;
  }
  return "";
};

const mediumDateStringFromDate = (calendar, date, options) => {
  if (options.calendarType === calendarTypes.CALENDAR_TYPE_DATE_TIME) {
    // We have not checked the date order here
    switch (date.get("precision")) {
      case Precision.Century:
      case Precision.Decade:
      case Precision.Year:
        return `${getPrintedYear(date.get("year"))}`;
      case Precision.Month:
        return `${getShortMonthName(calendar, date.get("month"))} ${getPrintedYear(date.get("year"))}`;
      default:
        return `${options.includeDayName ? getShortWeekdayName(calendar, date.get("weekday")) + " " : ""}${date.get(
          "day"
        )} ${getShortMonthName(calendar, date.get("month"))} ${getPrintedYear(date.get("year"))}`;
    }
  } else if (options.calendarType === calendarTypes.CALENDAR_TYPE_WEEKDAY_TIME) {
    switch (date.get("precision")) {
      case Precision.Century:
      case Precision.Decade:
      case Precision.Year:
      case Precision.Month:
      case Precision.Week:
        return `Week ${getWeeksSinceZeroDate(calendar, date)}`;
      default:
        return (
          `${getWeekdayName(calendar, date.get("weekday")) + " "}` + `Week ${getWeeksSinceZeroDate(calendar, date)}`
        );
    }
  } else if (options.calendarType === calendarTypes.CALENDAR_TYPE_DAY_TIME) {
    return `Day ${getDaysSinceZeroDate(calendar, date)}`;
  }
  return "";
};

const shortDateStringFromDate = (calendar, date, dateOrder, options) => {
  if (options.calendarType === calendarTypes.CALENDAR_TYPE_DATE_TIME) {
    return dateOrder === DateOrderIdentifiers.YearMonthDay
      ? shortDateStringFromDateOrderYMD(calendar, date, options.includeDayName)
      : dateOrder === DateOrderIdentifiers.DayMonthYear
        ? shortDateStringFromDateOrderDMY(calendar, date, options.includeDayName)
        : shortDateStringFromDateOrderMDY(calendar, date, options.includeDayName);
  } else if (options.calendarType === calendarTypes.CALENDAR_TYPE_WEEKDAY_TIME) {
    switch (date.get("precision")) {
      case Precision.Century:
      case Precision.Decade:
      case Precision.Year:
      case Precision.Month:
      case Precision.Week:
        return `Wk ${getWeeksSinceZeroDate(calendar, date)}`;
      default:
        return (
          `${getShortWeekdayName(calendar, date.get("weekday")) + " "}` + `Wk ${getWeeksSinceZeroDate(calendar, date)}`
        );
    }
  } else if (options.calendarType === calendarTypes.CALENDAR_TYPE_DAY_TIME) {
    return `D ${getDaysSinceZeroDate(calendar, date)}`;
  }
  return "";
};

const shortDateStringFromDateOrderYMD = (calendar, date, includeDayName) => {
  switch (date.get("precision")) {
    case Precision.Century:
    case Precision.Decade:
    case Precision.Year:
      return `${getPrintedYear(date.get("year"))}`;
    case Precision.Month:
      return `${date.get("year")}-${twoDigits(date.get("month"))}`;
    default:
      return `${includeDayName ? getShortWeekdayName(calendar, date.get("weekday")) + " " : ""}${date.get(
        "year"
      )}-${twoDigits(date.get("month"))}-${twoDigits(date.get("day"))}`;
  }
};

const shortDateStringFromDateOrderDMY = (calendar, date, includeDayName) => {
  switch (date.get("precision")) {
    case Precision.Century:
    case Precision.Decade:
    case Precision.Year:
      return `${getPrintedYear(date.get("year"))}`;
    case Precision.Month:
      return `${twoDigits(date.get("month"))}/${date.get("year")}`;
    default:
      return `${includeDayName ? getShortWeekdayName(calendar, date.get("weekday")) + " " : ""}${twoDigits(
        date.get("day")
      )}/${twoDigits(date.get("month"))}/${date.get("year")}`;
  }
};

const shortDateStringFromDateOrderMDY = (calendar, date, includeDayName) => {
  switch (date.get("precision")) {
    case Precision.Century:
    case Precision.Decade:
    case Precision.Year:
      return `${getPrintedYear(date.get("year"))}`;
    case Precision.Month:
      return `${twoDigits(date.get("month"))}/${date.get("year")}`;
    default:
      return `${includeDayName ? getShortWeekdayName(calendar, date.get("weekday")) + " " : ""}${twoDigits(
        date.get("month")
      )}/${twoDigits(date.get("day"))}/${date.get("year")}`;
  }
};

const getTimeStringForRelativeTime = (calendar, date) => {
  const tsSpan = date.timestamp - calendar.zeroDateTimestamp;
  const hour = getHoursSinceZeroDate(calendar, date);
  const hourStr = Math.abs(hour);
  const minute = dateSubtractionFunctions.minutesBetweenTS(0, tsSpan - hour * 3600);
  const minuteStr = Math.abs(minute);
  const second = dateSubtractionFunctions.secondsBetweenTS(0, tsSpan - hour * 3600 - minute * 60);
  const secondStr = Math.abs(second);
  let str = "";
  if (date.get("precision") <= Precision.Hour) {
    str = `${hourStr}:00`;
  } else {
    switch (date.get("precision")) {
      case Precision.Minute: {
        str = `${hourStr}:${twoDigits(minuteStr)}`;
        break;
      }
      case Precision.Second:
        str = `${hourStr}:${twoDigits(minuteStr)}:${twoDigits(secondStr)}`;
        break;
      default:
        break;
    }
  }
  str = `${hour < 0 || minute < 0 || second < 0 ? "-" : ""}${str}`;
  return str;
};

const timeStringFromDate = (calendar, date, options, is2400 = false) => {
  const timeFormat = options.timeFormat;
  if (options.calendarType !== calendarTypes.CALENDAR_TYPE_HOUR_TIME) {
    const hoursInDay = getHoursInDayFromCalendar(calendar);
    let hour = date.hour;
    let amPm = "";
    if (timeFormat === TimeFormatIdentifiers.Twelve) {
      amPm = getAmPmString(hour, hoursInDay);
      hour = getTwelveHourString(hour, hoursInDay);
    } else if (is2400) {
      hour = hoursInDay;
    }
    switch (date.get("precision")) {
      case Precision.Hour:
        return timeFormat === TimeFormatIdentifiers.Twelve ? `${hour}${amPm}` : `${hour}:00`;
      case Precision.Minute:
        return `${hour}:${twoDigits(date.get("minute"))}${amPm}`;
      case Precision.Second:
        return `${hour}:${twoDigits(date.get("minute"))}:${twoDigits(date.get("second"))}${amPm}`;
      default:
        return "";
    }
  } else {
    return getTimeStringForRelativeTime(calendar, date);
  }
};

const stringFromRelativeDateRange = (
  calendar,
  startDate,
  endDate,
  options,
  hasUncertainStart,
  hasUncertainEnd,
  ongoing
) => {
  let finalStr = "";
  // Adjust precision
  const usePrecision = Math.max(startDate.precision, endDate.precision);
  const useStartDate = startDate.set("precision", usePrecision);
  const useEndDate = endDate.set("precision", usePrecision);
  let weekStr = ``;
  let dayStr = ``;
  let timeStr = ``;
  const isShortFormat = !options.dateFormat === DateFormatIdentifiers.Long;
  const startPrefix = hasUncertainStart ? "~" : "";
  const endPrefix = hasUncertainEnd ? "~" : "";

  switch (options.calendarType) {
    case calendarTypes.CALENDAR_TYPE_WEEKDAY_TIME: {
      // week vars
      const startWeek = getWeeksSinceZeroDate(calendar, useStartDate);
      const endWeek = getWeeksSinceZeroDate(calendar, useEndDate);
      const isSameWeek = startWeek === endWeek;
      weekStr = `${startWeek}${!isSameWeek ? ` - ${endWeek}` : ``}`;
      // weekday vars
      const weekLabel = isShortFormat ? `Wk` : `Week`;
      const startWeekday = getWeekdayName(calendar, useStartDate.get("weekday"));
      const endWeekday = getWeekdayName(calendar, useEndDate.get("weekday"));
      const isSameWeekDay = startWeekday === endWeekday;
      if (ongoing) {
        dayStr = `${startWeekday}`;
      } else {
        dayStr = `${startWeekday}${!isSameWeekDay ? ` - ${endWeekday}` : ``}`;
      }

      if (usePrecision <= Precision.Week) {
        finalStr = `${startPrefix}${weekLabel} ${weekStr}`;
        if (ongoing) {
          finalStr += ` - Present`;
        }
      } else if (usePrecision === Precision.Day) {
        if (ongoing) {
          finalStr = `${startPrefix}${dayStr} ${weekLabel} ${startWeek} - Present`;
        } else if (isSameWeek) {
          finalStr = `${startPrefix}${dayStr} ${weekLabel} ${weekStr}`;
        } else {
          finalStr = `${startPrefix}${startWeekday} ${weekLabel} ${startWeek} - ${endPrefix}${endWeekday} ${weekLabel} ${endWeek}`;
        }
      } else if (usePrecision > Precision.Day) {
        const startTime = timeStringFromDate(calendar, useStartDate, options);
        const endTime = timeStringFromDate(calendar, useEndDate, options);
        timeStr = `${startTime}${startTime !== endTime ? ` - ${endTime}` : ``}`;
        if (ongoing) {
          finalStr = `${startPrefix}${startTime} ${startWeekday} ${weekLabel} ${startWeek} - Present`;
        } else if (isSameWeek && isSameWeekDay) {
          finalStr = `${startPrefix}${timeStr} ${dayStr} ${weekLabel} ${weekStr}`;
        } else if (!isSameWeekDay && isSameWeek) {
          finalStr = `${startPrefix}${startTime} ${startWeekday} - ${endPrefix}${endTime} ${endWeekday} ${weekLabel} ${weekStr}`;
        } else {
          finalStr =
            `${startPrefix}${startTime} ${startWeekday} ${weekLabel} ${startWeek}` +
            ` - ${endPrefix}${endTime} ${endWeekday} ${weekLabel} ${endWeek}`;
        }
      }
      break;
    }
    case calendarTypes.CALENDAR_TYPE_DAY_TIME: {
      // day vars
      const dayLabel = `Day`;
      const startDay = getDaysSinceZeroDate(calendar, useStartDate);
      const endDay = getDaysSinceZeroDate(calendar, useEndDate);
      const isSameDay = startDay === endDay;
      if (ongoing) {
        dayStr = `${startDay}`;
      } else {
        dayStr = `${startDay}${!isSameDay ? ` - ${endDay}` : ``}`;
      }

      if (usePrecision <= Precision.Day) {
        finalStr = `${startPrefix}${dayLabel} ${dayStr}`;
        if (ongoing) {
          finalStr += ` - Present`;
        }
      } else if (usePrecision > Precision.Day) {
        const startTime = timeStringFromDate(calendar, useStartDate, options);
        const endTime = timeStringFromDate(calendar, useEndDate, options);
        timeStr = `${startTime}${startTime !== endTime ? ` - ${endTime}` : ``}`;
        if (ongoing) {
          finalStr = `${startPrefix}${startTime} ${dayLabel} ${startDay} - Present`;
        } else if (isSameDay) {
          finalStr = `${startPrefix}${timeStr} ${dayLabel} ${dayStr}`;
        } else {
          finalStr =
            `${startPrefix}${startTime} ${dayLabel} ${startDay}` + ` - ${endPrefix}${endTime} ${dayLabel} ${endDay}`;
        }
      }
      break;
    }
    case calendarTypes.CALENDAR_TYPE_HOUR_TIME: {
      const startTime = getTimeStringForRelativeTime(calendar, useStartDate);
      const endTime = getTimeStringForRelativeTime(calendar, useEndDate);
      if (ongoing) {
        finalStr = `${startPrefix}${startTime} - Present`;
      } else {
        finalStr = `${startPrefix}${startTime}${startTime !== endTime ? ` - ${endPrefix}${endTime}` : ``}`;
      }
      break;
    }
    default:
      break;
  }
  return finalStr;
};

const stringFromAbsoluteDateRange = (
  calendar,
  startDate,
  endDate,
  options,
  adjustEndDateFor2400 = true,
  hasUncertainStart,
  hasUncertainEnd,
  ongoing
) => {
  // TODO: This function still feels like a complete mess. Would love to refactor it further
  // to make it a little cleaner...
  // We know the dates are not the same, so let's work out our compression options.
  const startEra = startDate.era;
  const startYear = startDate.year;
  const startMonth = startDate.month;
  const startDay = startDate.day;
  const startWeekday = startDate.weekday;
  const startPrecision = startDate.precision;
  const adjustmentPrecision = Math.min(Precision.Day, endDate.precision);

  const is2400 = getIs2400(startDate, endDate, options.timeFormat);

  const useEndDate =
    adjustEndDateFor2400 && is2400
      ? dateByAddingSpan(calendar, endDate, getReverseTimespan(timespanForPrecision(adjustmentPrecision)))
      : endDate;

  const endEra = useEndDate.era;
  const endYear = useEndDate.year;
  const endMonth = useEndDate.month;
  const endDay = useEndDate.day;
  const endPrecision = useEndDate.precision;
  const weekdayFn = options.dateFormat === DateFormatIdentifiers.Long ? getWeekdayName : getShortWeekdayName;
  const monthFn = options.dateFormat === DateFormatIdentifiers.Long ? getMonthName : getShortMonthName;

  const se = getShortEraNameIfNotLast(calendar, startEra);
  const startEraString = se ? ` ${se}` : "";
  const maxPrecision = Math.max(startPrecision, endPrecision);
  const adjustPrecisionFunc = p => (p < maxPrecision && p <= Precision.Day ? Precision.Day + 1 : p);
  const startDateWithFixedPrecision = startDate.update("precision", adjustPrecisionFunc);
  const endDateWithFixedPrecision = useEndDate.update("precision", adjustPrecisionFunc);
  const startPrefix = hasUncertainStart ? "~" : "";
  const endPrefix = hasUncertainEnd ? "~" : "";

  if ((startYear === endYear && startMonth === endMonth && startDay === endDay && startEra === endEra) || ongoing) {
    // If they are the same date - print the date in full, and then the timeString
    // Note: we know in this case the dates are different, and therefore, we know that the times
    // must differ if the dates don't, so we can print time without any extra checks
    const timeStr =
      startPrecision > Precision.Day || endPrecision > Precision.Day
        ? timeStringFromDate(calendar, startDateWithFixedPrecision, options) +
          (!ongoing ? "-" + timeStringFromDate(calendar, endDateWithFixedPrecision, options, is2400) : "") +
          " "
        : "";

    return (
      `${startPrefix}${timeStr}` +
      `${dateStringFromDate(calendar, startDate.set("precision", maxPrecision), options)}${startEraString}` +
      `${ongoing ? " - Present" : ""}`
    );
  }

  // The year and month are the same, but the day is different
  if (startYear === endYear && startMonth === endMonth && startEra === endEra) {
    // If there is no time component, Desired format is d-d MMM YYYY...
    if (startPrecision <= Precision.Day && endPrecision <= Precision.Day) {
      if (options.includeDayName) {
        return `${startPrefix}${weekdayFn(calendar, startWeekday)} ${startDay} - ${endPrefix}${dateStringFromDate(
          calendar,
          useEndDate,
          options
        )}${startEraString}`;
      } else {
        return `${startPrefix}${startDay}${endPrefix ? " - " : "-"}${endPrefix}${dateStringFromDate(
          calendar,
          useEndDate,
          options
        )}${startEraString}`;
      }
    } else {
      // If there is a time component, desired format is t d MMM  - t d MMM YYYY
      if (options.includeDayName) {
        return `${startPrefix}${timeStringFromDate(calendar, startDateWithFixedPrecision, options)} ${weekdayFn(
          calendar,
          startWeekday
        )} ${startDay} ${monthFn(calendar, startMonth)} - ${endPrefix}${timeStringFromDate(
          calendar,
          endDateWithFixedPrecision,
          options,
          is2400
        )} ${dateStringFromDate(calendar, useEndDate, options)}${startEraString}`;
      } else {
        return `${startPrefix}${timeStringFromDate(
          calendar,
          startDateWithFixedPrecision,
          options
        )} ${startDay} ${monthFn(calendar, startMonth)} - ${endPrefix}${timeStringFromDate(
          calendar,
          endDateWithFixedPrecision,
          options,
          is2400
        )} ${dateStringFromDate(calendar, useEndDate, options)}${startEraString}`;
      }
    }
  }

  // The years are the same, but the month is different
  if (startYear === endYear && startEra === endEra) {
    if (startPrecision <= Precision.Month && endPrecision <= Precision.Month) {
      // Month-only format... desired format is MMM-MMM YYYY
      return `${startPrefix}${monthFn(calendar, startMonth)}-${endPrefix}${dateStringFromDate(
        calendar,
        useEndDate,
        options
      )}${startEraString}`;
    }
    // If there is no time component, Desired format is d MMM - d MMM YYYY...
    if (startPrecision <= Precision.Day && endPrecision <= Precision.Day) {
      if (options.includeDayName) {
        return `${startPrefix}${weekdayFn(calendar, startWeekday)} ${startDay} ${monthFn(
          calendar,
          startMonth
        )} - ${endPrefix}${dateStringFromDate(calendar, useEndDate, options)}${startEraString}`;
      } else {
        return `${startPrefix}${startDay} ${monthFn(calendar, startMonth)} - ${endPrefix}${dateStringFromDate(
          calendar,
          useEndDate,
          options
        )}${startEraString}`;
      }
    } else {
      // If there is a time component, desired format is t d MMM  - t d MMM YYYY
      if (options.includeDayName) {
        return `${startPrefix}${timeStringFromDate(calendar, startDate, options)} ${weekdayFn(
          calendar,
          startWeekday
        )} ${startDay} ${monthFn(calendar, startMonth)} - ${endPrefix}${timeStringFromDate(
          calendar,
          useEndDate,
          options,
          is2400
        )} ${dateStringFromDate(calendar, useEndDate, options)}${startEraString}`;
      } else {
        return `${startPrefix}${timeStringFromDate(calendar, startDate, options)} ${startDay} ${monthFn(
          calendar,
          startMonth
        )} - ${endPrefix}${timeStringFromDate(calendar, useEndDate, options, is2400)} ${dateStringFromDate(
          calendar,
          useEndDate,
          options
        )}${startEraString}`;
      }
    }
  }

  // There is absolutely no overlap... so just print out dates in full...
  const ee = getShortEraName(calendar, endEra);
  const endEraString = endEra === startEra ? startEraString : ee ? ` ${ee}` : "";
  let startPart,
    endTimePart = "";
  if (startPrecision <= Precision.Year && endPrecision <= Precision.Year) {
    // Year-only format... desired format is YYYY-YYYY
    startPart = `${getPrintedYear(startYear)}${startEraString}`;
  } else if (startPrecision <= Precision.Month && endPrecision <= Precision.Month) {
    // Month-only format... desired format is MMM YYYY - MMM YYYY
    startPart = `${monthFn(calendar, startMonth)} ${startYear}${startEraString}`;
  } else if (startPrecision <= Precision.Day && endPrecision <= Precision.Day) {
    // Day-only format... desired format is d MMM YYYY - d MMM YYYY
    if (options.includeDayName) {
      startPart = `${weekdayFn(calendar, startWeekday)} ${startDay} ${monthFn(
        calendar,
        startMonth
      )} ${startYear}${startEraString}`;
    } else {
      startPart = `${startDay} ${monthFn(calendar, startMonth)} ${startYear}${startEraString}`;
    }
  } else {
    // Day+Time format... desired format is HH:MM d MMM YYYY - HH:MM d MMM YYYY
    endTimePart = `${timeStringFromDate(calendar, useEndDate, options, is2400)} `;
    if (options.includeDayName) {
      startPart = `${timeStringFromDate(calendar, startDate, options)} ${weekdayFn(
        calendar,
        startWeekday
      )} ${startDay} ${monthFn(calendar, startMonth)} ${startYear}${startEraString}`;
    } else {
      startPart = `${timeStringFromDate(calendar, startDate, options)} ${startDay} ${monthFn(
        calendar,
        startMonth
      )} ${startYear}${startEraString}`;
    }
  }

  return `${startPrefix}${startPart} - ${endPrefix}${endTimePart}${dateStringFromDate(
    calendar,
    useEndDate,
    options
  )}${endEraString}`;
};
